import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NcsColorPicker,
  findNcsColorByHex,
  getNcsShortCode,
  type NcsColorOption,
} from '../../../../features/ncsColor';
import {
  panelAppearanceRowClassName,
  panelColorSwatchClassName,
  panelRowLabelClassName,
} from '../propertyPanelUtils';

type FillColorControlProps = {
  label?: string;
  value: string;
  mixed?: boolean;
  mixedLabel?: string;
  onChange: (value: string) => void;
  /** Called after NCS select, and when the native color input blurs. */
  onCommit?: () => void;
  onClear?: () => void;
  pinnedColors?: NcsColorOption[];
  disabled?: boolean;
};

/**
 * Compact fill row: color swatch + matching NCS code (when any).
 * Click expands the NCS card (selected color + color picker on top; palette
 * grid stays collapsed until opened with +). When no NCS color matches, the
 * closed row mirrors the open picker: red-slash swatch + “None selected”.
 */
const FillColorControl: React.FC<FillColorControlProps> = ({
  label,
  value,
  mixed = false,
  mixedLabel,
  onChange,
  onCommit,
  onClear,
  pinnedColors,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const resolvedLabel = label ?? t('common.color');
  const resolvedMixedLabel = mixedLabel ?? t('common.mixed');

  const matchedNcs = useMemo(
    () => (mixed ? undefined : findNcsColorByHex(value)),
    [mixed, value],
  );
  const ncsShortCode = matchedNcs ? getNcsShortCode(matchedNcs) : undefined;
  const hasNcsSelection = Boolean(matchedNcs);

  return (
    <div className="py-0.5">
      <div className={panelAppearanceRowClassName}>
        <div className={panelRowLabelClassName}>{resolvedLabel}</div>
        <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
          {mixed && (
            <div className="text-[11px] text-gray-400">{resolvedMixedLabel}</div>
          )}
          {!mixed && hasNcsSelection && ncsShortCode && (
            <button
              type="button"
              className="max-w-[9rem] truncate font-mono text-[11px] font-medium text-gray-700 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => setExpanded((prev) => !prev)}
              disabled={disabled}
              title={matchedNcs?.name}
              aria-expanded={expanded}
              aria-label={t('propertyPanel.ncsShow')}
            >
              {ncsShortCode}
            </button>
          )}
          {!mixed && !hasNcsSelection && (
            <button
              type="button"
              className="max-w-[9rem] truncate text-[11px] font-medium text-gray-700 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => setExpanded((prev) => !prev)}
              disabled={disabled}
              aria-expanded={expanded}
              aria-label={t('propertyPanel.ncsShow')}
            >
              {t('propertyPanel.ncsNoneSelected')}
            </button>
          )}
          <button
            type="button"
            className={`${panelColorSwatchClassName} relative overflow-hidden bg-white`}
            style={{
              backgroundColor: mixed || !hasNcsSelection ? undefined : value,
            }}
            onClick={() => setExpanded((prev) => !prev)}
            disabled={disabled}
            aria-expanded={expanded}
            aria-label={
              expanded ? t('propertyPanel.ncsHide') : t('propertyPanel.ncsShow')
            }
            title={expanded ? t('propertyPanel.ncsHide') : t('propertyPanel.ncsShow')}
            data-testid="fill-color-swatch"
            data-ncs-matched={hasNcsSelection ? 'true' : 'false'}
          >
            {mixed ? (
              <span className="absolute inset-0 bg-[linear-gradient(135deg,#e5e7eb_25%,#fff_25%,#fff_50%,#e5e7eb_50%,#e5e7eb_75%,#fff_75%)] bg-[length:8px_8px]" />
            ) : !hasNcsSelection ? (
              <span className="absolute left-1/2 top-1/2 h-[141%] w-0.5 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-red-500" />
            ) : null}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mb-1 border-t border-gray-100 pt-1">
          <NcsColorPicker
            value={mixed ? undefined : value}
            pinnedColors={pinnedColors}
            defaultOpen={false}
            onSelect={(hex) => {
              onChange(hex);
              onCommit?.();
            }}
            onColorChange={onChange}
            onColorBlur={onCommit}
            onClear={onClear}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

export default FillColorControl;
